import axios from 'axios';
import { getItemToken, getItem } from '../../Utils/localStorageUtils';

const defaultDomain = `${process.env.REACT_APP_Baseurl}`;
const defaultPreFix = 'Front-End';
const finalRouteApi = `${defaultDomain}/${defaultPreFix}`;

// API Endpoints
const radioStationListApi = `${finalRouteApi}/Radio-station-list`;
const radioStationDetailsApi = `${finalRouteApi}/Radio-station-details`;

// Default headers
const getHeaders = () => {
    const token = getItemToken('access_token');
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : { 'guestverfiykey': 'WebVodWebnexs' })
    };
};

// No static data - everything should come from API

/**
 * Get list of radio stations with pagination and filtering
 * @param {Object} params - Request parameters
 * @param {string} params.Login_device_type - Device type (web, Mobile_Android, Mobile_IOS, TV)
 * @param {number} params.Login_user_type - User type (1,2,3,7,9)
 * @param {number|null} params.Login_user_id - User ID (required for logged in users)
 * @param {number} [params.limit=20] - Number of stations per page
 * @param {number} [params.offset=0] - Offset for pagination
 * @param {string} [params.search] - Search term for station title/description
 * @param {string} [params.category_id] - Category ID filter
 * @param {string} [params.language_id] - Language ID filter
 * @returns {Promise} API response with radio stations list
 */
export const getRadioStationList = async (params = {}) => {
    try {
        const defaultParams = {
            Login_device_type: 'web',
            Login_user_type: parseInt(getItem('role'), 10) || 9,
            Login_user_id: parseInt(getItem('user_id'), 10) || null,
            limit: 20,
            offset: 0
        };

        const requestBody = { ...defaultParams, ...params };

        // Use POST method as per backend specification
        const response = await axios.post(radioStationListApi, requestBody, {
            headers: getHeaders()
        });

        return response;
    } catch (error) {
        console.error('Error fetching radio station list:', error);
        
        // Return a structured error response with fallback data
        return {
            data: {
                status: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch radio stations',
                data: [], // No fallback data - everything should come from API
                pagination: {
                    total: 9,
                    limit: params.limit || 20,
                    offset: params.offset || 0,
                    hasMore: false
                }
            }
        };
    }
};

/**
 * Get detailed information about a specific radio station
 * @param {Object} params - Request parameters
 * @param {string} params.slug - Radio station slug
 * @param {string} [params.Login_device_type='web'] - Device type
 * @param {number} [params.Login_user_type=9] - User type
 * @param {number|null} [params.Login_user_id=null] - User ID
 * @returns {Promise} API response with radio station details
 */
export const getRadioStationDetails = async (params = {}) => {
    try {
        const defaultParams = {
            Login_device_type: 'web',
            Login_user_type: parseInt(getItem('role'), 10) || 1,
            Login_user_id: parseInt(getItem('user_id'), 10) || 1
        };

        const requestBody = { ...defaultParams, ...params };

        // Validate required parameters
        if (!requestBody.slug) {
            throw new Error('Station slug is required');
        }

        const response = await axios.post(radioStationDetailsApi, requestBody, {
            headers: getHeaders()
        });

        // DEBUG: Log the raw API response to see what fields are actually returned
        console.log('📡 RAW API RESPONSE DEBUG:', {
            apiEndpoint: radioStationDetailsApi,
            requestBody,
            responseStatus: response.status,
            responseData: response.data,
            dataFields: response.data?.data ? Object.keys(response.data.data) : 'No data object',
            audioFields: response.data?.data ? Object.keys(response.data.data).filter(key => 
                key.includes('url') || 
                key.includes('stream') || 
                key.includes('audio') || 
                key.includes('mp3') || 
                key.includes('aac')
            ) : 'No audio fields found'
        });

        return response;
    } catch (error) {
        console.error('Error fetching radio station details:', error);
        
        // Return a structured error response
        return {
            data: {
                status: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch radio station details',
                data: null
            }
        };
    }
};

/**
 * Add radio station to user's wishlist
 * @param {Object} params - Request parameters
 * @param {number} params.source_id - Radio station ID
 * @param {number} [params.source_type=2] - Source type (2 for radio stations)
 * @param {string} [params.Login_device_type='web'] - Device type
 * @param {number} params.Login_user_type - User type
 * @param {number} params.Login_user_id - User ID
 * @returns {Promise} API response
 */
export const addToWishlist = async (params = {}) => {
    try {
        const wishlistApi = `${finalRouteApi}/Wishlist-Update`;
        
        const defaultParams = {
            Login_device_type: 'web',
            source_type: 2
        };

        const requestBody = { ...defaultParams, ...params };

        // Validate required parameters
        if (!requestBody.source_id) {
            throw new Error('Station ID is required');
        }
        if (!requestBody.Login_user_id) {
            throw new Error('User must be logged in');
        }

        const response = await axios.post(wishlistApi, requestBody, {
            headers: getHeaders()
        });

        return response;
    } catch (error) {
        console.error('Error updating wishlist:', error);
        return {
            data: {
                status: false,
                message: error.response?.data?.message || error.message || 'Failed to update wishlist'
            }
        };
    }
};

/**
 * Add radio station to user's watch later list
 * @param {Object} params - Request parameters
 * @param {number} params.source_id - Radio station ID
 * @param {number} [params.source_type=2] - Source type (2 for radio stations)
 * @param {string} [params.Login_device_type='web'] - Device type
 * @param {number} params.Login_user_type - User type
 * @param {number} params.Login_user_id - User ID
 * @returns {Promise} API response
 */
export const addToWatchLater = async (params = {}) => {
    try {
        const watchLaterApi = `${finalRouteApi}/Watchlater-Update`;
        
        const defaultParams = {
            Login_device_type: 'web',
            source_type: 2
        };

        const requestBody = { ...defaultParams, ...params };

        // Validate required parameters
        if (!requestBody.source_id) {
            throw new Error('Station ID is required');
        }
        if (!requestBody.Login_user_id) {
            throw new Error('User must be logged in');
        }

        const response = await axios.post(watchLaterApi, requestBody, {
            headers: getHeaders()
        });

        return response;
    } catch (error) {
        console.error('Error updating watch later:', error);
        return {
            data: {
                status: false,
                message: error.response?.data?.message || error.message || 'Failed to update watch later'
            }
        };
    }
};

/**
 * Like or dislike a radio station
 * @param {Object} params - Request parameters
 * @param {number} params.source_id - Radio station ID
 * @param {number} [params.source_type=2] - Source type (2 for radio stations)
 * @param {number} params.like_status - Like status (1 for like, 0 for unlike)
 * @param {string} [params.Login_device_type='web'] - Device type
 * @param {number} params.Login_user_type - User type
 * @param {number} params.Login_user_id - User ID
 * @returns {Promise} API response
 */
export const updateLike = async (params = {}) => {
    try {
        const likeApi = `${finalRouteApi}/Like-Update`;
        
        const defaultParams = {
            Login_device_type: 'web',
            source_type: 2
        };

        const requestBody = { ...defaultParams, ...params };

        // Validate required parameters
        if (!requestBody.source_id) {
            throw new Error('Station ID is required');
        }
        if (!requestBody.Login_user_id) {
            throw new Error('User must be logged in');
        }
        if (typeof requestBody.like_status === 'undefined') {
            throw new Error('Like status is required');
        }

        const response = await axios.post(likeApi, requestBody, {
            headers: getHeaders()
        });

        return response;
    } catch (error) {
        console.error('Error updating like:', error);
        return {
            data: {
                status: false,
                message: error.response?.data?.message || error.message || 'Failed to update like'
            }
        };
    }
};

/**
 * Dislike a radio station
 * @param {Object} params - Request parameters
 * @param {number} params.source_id - Radio station ID
 * @param {number} [params.source_type=2] - Source type (2 for radio stations)
 * @param {number} params.dislike_status - Dislike status (1 for dislike, 0 for undislike)
 * @param {string} [params.Login_device_type='web'] - Device type
 * @param {number} params.Login_user_type - User type
 * @param {number} params.Login_user_id - User ID
 * @returns {Promise} API response
 */
export const updateDislike = async (params = {}) => {
    try {
        const dislikeApi = `${finalRouteApi}/Dislike-Update`;
        
        const defaultParams = {
            Login_device_type: 'web',
            source_type: 2
        };

        const requestBody = { ...defaultParams, ...params };

        // Validate required parameters
        if (!requestBody.source_id) {
            throw new Error('Station ID is required');
        }
        if (!requestBody.Login_user_id) {
            throw new Error('User must be logged in');
        }
        if (typeof requestBody.dislike_status === 'undefined') {
            throw new Error('Dislike status is required');
        }

        const response = await axios.post(dislikeApi, requestBody, {
            headers: getHeaders()
        });

        return response;
    } catch (error) {
        console.error('Error updating dislike:', error);
        return {
            data: {
                status: false,
                message: error.response?.data?.message || error.message || 'Failed to update dislike'
            }
        };
    }
};

/**
 * Record radio station listening activity
 * @param {Object} params - Request parameters
 * @param {number} params.source_id - Radio station ID
 * @param {number} [params.source_type=2] - Source type (2 for radio stations)
 * @param {string} [params.Login_device_type='web'] - Device type
 * @param {number} params.Login_user_type - User type
 * @param {number|null} params.Login_user_id - User ID
 * @param {string|null} [params.Login_guest_user] - Guest user IP (for guests)
 * @returns {Promise} API response
 */
export const recordListeningActivity = async (params = {}) => {
    try {
        const activityApi = `${finalRouteApi}/continue-watching-recent-view-store-update`;
        
        const defaultParams = {
            Login_device_type: 'web',
            source_type: 2
        };

        const requestBody = { ...defaultParams, ...params };

        // Validate required parameters
        if (!requestBody.source_id) {
            throw new Error('Station ID is required');
        }

        const response = await axios.post(activityApi, requestBody, {
            headers: getHeaders()
        });

        return response;
    } catch (error) {
        console.error('Error recording listening activity:', error);
        return {
            data: {
                status: false,
                message: error.response?.data?.message || error.message || 'Failed to record listening activity'
            }
        };
    }
};

/**
 * Search radio stations
 * @param {Object} params - Search parameters
 * @param {string} params.search - Search query
 * @param {number} [params.limit=20] - Number of results per page
 * @param {number} [params.offset=0] - Offset for pagination
 * @returns {Promise} API response with search results
 */
export const searchRadioStations = async (params = {}) => {
    if (!params.search || params.search.trim() === '') {
        return {
            data: {
                status: true,
                data: [],
                pagination: {
                    total: 0,
                    limit: params.limit || 20,
                    offset: params.offset || 0,
                    hasMore: false
                }
            }
        };
    }

    return getRadioStationList(params);
};

/**
 * Get radio stations by category
 * @param {Object} params - Request parameters
 * @param {string} params.category_id - Category ID
 * @param {number} [params.limit=20] - Number of stations per page
 * @param {number} [params.offset=0] - Offset for pagination
 * @returns {Promise} API response with filtered stations
 */
export const getRadioStationsByCategory = async (params = {}) => {
    if (!params.category_id) {
        throw new Error('Category ID is required');
    }

    return getRadioStationList(params);
};

/**
 * Get radio stations by language
 * @param {Object} params - Request parameters
 * @param {string} params.language_id - Language ID
 * @param {number} [params.limit=20] - Number of stations per page
 * @param {number} [params.offset=0] - Offset for pagination
 * @returns {Promise} API response with filtered stations
 */
export const getRadioStationsByLanguage = async (params = {}) => {
    if (!params.language_id) {
        throw new Error('Language ID is required');
    }

    return getRadioStationList(params);
};

/**
 * Get detailed information about a specific radio station (wrapper for detail page)
 * @param {string} stationSlug - Radio station slug
 * @returns {Promise} API response with radio station details
 */
export const getStationDetails = async (stationSlug) => {
    const response = await getRadioStationDetails({ slug: stationSlug });
    return {
        success: response.data?.status || false,
        data: response.data?.data || null,
        message: response.data?.message || 'Failed to fetch station details'
    };
};

/**
 * Toggle like status for a radio station
 * @param {number} stationId - Radio station ID
 * @param {boolean} isLiked - New like status
 * @returns {Promise} API response
 */
export const toggleLike = async (stationId, isLiked) => {
    const response = await updateLike({
        source_id: stationId,
        like_status: isLiked ? 1 : 0,
        Login_user_type: parseInt(getItem('role'), 10) || 9,
        Login_user_id: parseInt(getItem('user_id'), 10) || null
    });
    return {
        success: response.data?.status || false,
        message: response.data?.message || 'Failed to update like status'
    };
};

/**
 * Record listening activity for a radio station (wrapper for detail page)
 * @param {number} stationId - Radio station ID
 * @param {string} activity - Activity type (e.g., 'play', 'pause')
 * @returns {Promise} API response
 */
export const recordActivity = async (stationId, activity) => {
    const response = await recordListeningActivity({
        source_id: stationId,
        Login_user_type: parseInt(getItem('role'), 10) || 9,
        Login_user_id: parseInt(getItem('user_id'), 10) || null
    });
    return {
        success: response.data?.status || false,
        message: response.data?.message || 'Failed to record activity'
    };
};

// Export all functions
const RadioStationDefaultApi = {
    getRadioStationList,
    getRadioStationDetails,
    getStationDetails,
    toggleLike,
    recordActivity,
    addToWishlist,
    addToWatchLater,
    updateLike,
    updateDislike,
    recordListeningActivity,
    searchRadioStations,
    getRadioStationsByCategory,
    getRadioStationsByLanguage
};
export default RadioStationDefaultApi;

// Also export as named export for different import styles
export const RadioStationApi = {
    getRadioStationList,
    getRadioStationDetails,
    getStationDetails,
    toggleLike,
    recordActivity,
    addToWishlist,
    addToWatchLater,
    updateLike,
    updateDislike,
    recordListeningActivity,
    searchRadioStations,
    getRadioStationsByCategory,
    getRadioStationsByLanguage
};
